import type { ElementType, ReactNode } from 'react';

import styles from './Typography.module.css';

export type TypographyVariant =
  | 'display-2xl'
  | 'display-xl'
  | 'display-lg'
  | 'display-md'
  | 'display-sm'
  | 'display-xs'
  | 'text-xl'
  | 'text-lg'
  | 'text-md'
  | 'text-sm'
  | 'text-xs';

export type TypographyWeight = 'regular' | 'medium' | 'semibold' | 'bold';

export interface TypographyProps {
  /** Type scale from the Display / Text token sets. */
  variant?: TypographyVariant;
  /** Font-weight axis, independent of the scale. */
  weight?: TypographyWeight;
  /** Rendered element or component — defaults to `span`. Use for semantic headings/paragraphs. */
  as?: ElementType;
  className?: string;
  /** Forwarded to the rendered element — e.g. to wire up `aria-describedby`/`aria-labelledby`. */
  id?: string;
  children: ReactNode;
}

/**
 * Text primitive that maps the typography tokens (scale × weight) onto any element.
 * Presentation only — `as` carries the semantics (e.g. `h1`, `p`).
 *
 * @interim Ported from the reference portal; replaceable by the UI Kit without API breakage.
 */
export function Typography({
  variant = 'text-md',
  weight = 'regular',
  as: Component = 'span',
  className,
  id,
  children,
}: TypographyProps) {
  const classes = [styles.typography, styles[variant], styles[weight], className]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classes} id={id}>
      {children}
    </Component>
  );
}
